import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, Instagram, Facebook } from "lucide-react";
import { LOGO, COMPANY, PORTFOLIO_IMAGES, HERO_COLLAGE } from "../lib/content";
import { api } from "../lib/api";

// =================== MEGA MENU MODEL ===================
const NAV = [
  { to: "/", label: "Home", kind: "link" },
  {
    label: "Studio",
    kind: "mega",
    feature: {
      title: "Where Vision Meets Prestige.",
      copy: "Editorial taste. Strategic discipline. National reach with a Hampton Roads heart.",
      image: HERO_COLLAGE[1],
      cta: { to: "/about", label: "About the Studio" },
    },
    columns: [
      {
        heading: "The Studio",
        links: [
          { to: "/about", label: "About", desc: "Studio ethos & principal" },
          { to: "/services", label: "Services", desc: "Disciplines we orchestrate" },
          { to: "/packages", label: "Packages", desc: "Starting frameworks" },
          { to: "/faq", label: "FAQ", desc: "Booking, policies & process" },
        ],
      },
    ],
  },
  {
    label: "Work",
    kind: "mega",
    feature: {
      title: "Selected Work.",
      copy: "Editorial portfolios, campaign archives, and the words of the principals we've served.",
      image: PORTFOLIO_IMAGES[0],
      cta: { to: "/portfolio", label: "Open the Portfolio" },
    },
    columns: [
      {
        heading: "Showcase",
        links: [
          { to: "/portfolio", label: "Portfolio", desc: "Recent engagements" },
          { to: "/testimonials", label: "Testimonials", desc: "On the record" },
        ],
      },
      {
        heading: "Editorial",
        links: [
          { to: "/press", label: "Press & Media", desc: "Where we've been featured" },
          { to: "/blog", label: "Insights", desc: "Editorial dispatches" },
        ],
      },
    ],
  },
  { to: "/creators", label: "Creator Network", kind: "link" },
  { to: "/contact", label: "Contact", kind: "link" },
];

// =================== ANNOUNCEMENT BANNER ===================
export function AnnouncementBanner() {
  const [announcements, setAnnouncements] = useState([]);
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    api.get("/announcements").then((r) => setAnnouncements(r.data)).catch(() => {});
  }, []);
  const item = announcements?.[0];
  if (!item || dismissed) return null;
  return (
    <div className="bg-[var(--crest-black)] text-[var(--crest-cream)] text-xs tracking-[0.2em] uppercase py-2.5 px-4 flex items-center justify-center gap-4" data-testid="announcement-banner">
      <span className="hidden md:inline" style={{ color: "var(--crest-gold)" }}>—</span>
      <span className="text-[11px] md:text-xs">{item.title} · {item.message}</span>
      {item.cta_url && (
        <a href={item.cta_url} className="underline" style={{ color: "var(--crest-gold)" }} data-testid="announcement-cta">{item.cta_label || "Learn more"}</a>
      )}
      <button onClick={() => setDismissed(true)} className="ml-2" aria-label="Dismiss" data-testid="announcement-dismiss">
        <X size={14} />
      </button>
    </div>
  );
}

// =================== MEGA PANEL ===================
function MegaPanel({ item, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.25, ease: [0.22, 0.61, 0.36, 1] }}
      className="absolute left-0 right-0 top-full bg-[var(--crest-cream)] border-t border-black/10 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.15)]"
      onMouseLeave={onClose}
      data-testid={`mega-panel-${item.label.toLowerCase()}`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-12 grid md:grid-cols-12 gap-10">
        {/* Columns */}
        <div className={`md:col-span-${item.columns.length > 1 ? "7" : "5"} grid md:grid-cols-${item.columns.length} gap-10`}>
          {item.columns.map((col) => (
            <div key={col.heading}>
              <div className="eyebrow text-[var(--crest-muted)] mb-5">— {col.heading}</div>
              <ul className="space-y-5">
                {col.links.map((l) => (
                  <li key={l.to}>
                    <Link
                      to={l.to}
                      onClick={onClose}
                      className="group inline-block"
                      data-testid={`mega-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <div className="font-serif text-2xl tracking-tight group-hover:text-[var(--crest-gold)] transition-colors">{l.label}</div>
                      {l.desc && <div className="text-xs tracking-wider text-[var(--crest-muted)] mt-1">{l.desc}</div>}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Feature image */}
        {item.feature && (
          <div className={`md:col-span-${item.columns.length > 1 ? "5" : "7"} md:col-start-auto`}>
            <Link to={item.feature.cta.to} onClick={onClose} className="block group">
              <div className="aspect-[16/9] overflow-hidden">
                <img src={item.feature.image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="mt-6 grid md:grid-cols-12 gap-6 items-end">
                <div className="md:col-span-8">
                  <div className="eyebrow text-[var(--crest-gold)]">— Featured</div>
                  <h4 className="font-serif text-2xl md:text-3xl tracking-tight mt-2 leading-tight">{item.feature.title}</h4>
                  <p className="text-sm text-[var(--crest-muted)] mt-2 leading-relaxed">{item.feature.copy}</p>
                </div>
                <div className="md:col-span-4 md:text-right">
                  <span className="btn-ghost-gold inline-flex">{item.feature.cta.label} →</span>
                </div>
              </div>
            </Link>
          </div>
        )}
      </div>
    </motion.div>
  );
}

// =================== HEADER ===================
export default function Header() {
  const [open, setOpen] = useState(false);
  const [mobileSection, setMobileSection] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState(null); // index of open mega
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
    setActive(null);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        className={`sticky top-0 z-40 backdrop-blur-xl transition-all border-b ${
          scrolled || active !== null
            ? "bg-[var(--crest-cream)]/95 border-black/10"
            : "bg-[var(--crest-cream)]/70 border-transparent"
        }`}
        data-testid="site-header"
        onMouseLeave={() => setActive(null)}
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between relative">
          <Link to="/" className="flex items-center" data-testid="header-logo">
            <img src={LOGO.threeDimensional} alt="Upper Crest Image" className="h-14 w-14 object-contain" />
          </Link>

          <nav className="hidden lg:flex items-center gap-8" data-testid="header-nav-desktop">
            {NAV.map((item, i) => {
              if (item.kind === "link") {
                return (
                  <NavLink
                    key={item.label}
                    to={item.to}
                    end={item.to === "/"}
                    onMouseEnter={() => setActive(null)}
                    className={({ isActive }) =>
                      `text-[0.72rem] tracking-[0.28em] uppercase transition-colors py-2 ${
                        isActive ? "text-[var(--crest-gold)]" : "text-[var(--crest-black)] hover:text-[var(--crest-gold)]"
                      }`
                    }
                    data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {item.label}
                  </NavLink>
                );
              }
              const isOpen = active === i;
              return (
                <button
                  key={item.label}
                  onMouseEnter={() => setActive(i)}
                  onFocus={() => setActive(i)}
                  className={`flex items-center gap-1.5 text-[0.72rem] tracking-[0.28em] uppercase transition-colors py-2 ${
                    isOpen ? "text-[var(--crest-gold)]" : "text-[var(--crest-black)] hover:text-[var(--crest-gold)]"
                  }`}
                  data-testid={`nav-mega-${item.label.toLowerCase()}`}
                  aria-expanded={isOpen}
                >
                  {item.label}
                  <ChevronDown size={12} className={`transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
                </button>
              );
            })}
          </nav>

          <div className="flex items-center gap-3">
            <Link to="/book" className="hidden md:inline-flex btn-primary" data-testid="header-book-cta">
              Book Consultation
            </Link>
            <button
              className="lg:hidden p-2 -mr-2"
              onClick={() => setOpen((v) => !v)}
              aria-label="Toggle menu"
              data-testid="mobile-menu-toggle"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>

          {/* Mega panels — positioned absolutely under header */}
          <AnimatePresence>
            {active !== null && NAV[active]?.kind === "mega" && (
              <MegaPanel item={NAV[active]} onClose={() => setActive(null)} />
            )}
          </AnimatePresence>
        </div>

        {/* MOBILE MENU */}
        {open && (
          <div className="lg:hidden border-t border-black/10 bg-[var(--crest-cream)]" data-testid="mobile-menu">
            <div className="px-6 py-4 divide-y divide-black/10">
              {NAV.map((item, i) => {
                if (item.kind === "link") {
                  return (
                    <NavLink
                      key={item.label}
                      to={item.to}
                      end={item.to === "/"}
                      className="block py-4 text-sm tracking-[0.25em] uppercase"
                      data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {item.label}
                    </NavLink>
                  );
                }
                const expanded = mobileSection === i;
                return (
                  <div key={item.label} className="py-2">
                    <button
                      onClick={() => setMobileSection(expanded ? null : i)}
                      className="w-full flex items-center justify-between py-3 text-sm tracking-[0.25em] uppercase"
                      data-testid={`mobile-nav-mega-${item.label.toLowerCase()}`}
                    >
                      {item.label}
                      <ChevronDown size={14} className={`transition-transform ${expanded ? "rotate-180" : ""}`} />
                    </button>
                    {expanded && (
                      <div className="pl-1 pb-4 space-y-2">
                        {item.columns.flatMap((col) => col.links).map((l) => (
                          <NavLink
                            key={l.to}
                            to={l.to}
                            className="block py-1.5 text-base font-serif tracking-tight"
                            data-testid={`mobile-mega-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                          >
                            {l.label}
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="px-6 pb-6">
              <Link to="/book" className="btn-primary w-full" data-testid="mobile-book-cta">Book Consultation</Link>
            </div>
          </div>
        )}
      </header>
    </>
  );
}

// =================== FOOTER ===================
export function Footer() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await api.post("/newsletter", { email });
      setStatus("Subscribed. Welcome.");
      setEmail("");
    } catch {
      setStatus("Could not subscribe — try again.");
    }
  };
  return (
    <footer className="bg-[#000000] text-[var(--crest-cream)] on-dark" data-testid="site-footer">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <Link to="/" className="inline-block" data-testid="footer-logo">
            <img src={LOGO.whiteOnBlack} alt="Upper Crest Image" className="h-20 w-20 object-contain" />
          </Link>
          <p className="mt-8 text-sm text-[var(--crest-cream)]/70 max-w-md leading-relaxed">
            Luxury branding, marketing, and consulting for organizations, public figures, productions, and corporations ready to lead.
          </p>
          <div className="mt-8 text-sm">
            <div className="eyebrow text-[var(--crest-gold)] mb-2">Serving</div>
            <div>{COMPANY.region}</div>
            <a href={`mailto:${COMPANY.email}`} className="block mt-1 hover:text-[var(--crest-gold)]" data-testid="footer-email">{COMPANY.email}</a>
          </div>
        </div>

        <div className="md:col-span-3">
          <div className="eyebrow text-[var(--crest-gold)] mb-4">Explore</div>
          <ul className="space-y-2 text-sm">
            {[
              { to: "/about", label: "About" },
              { to: "/services", label: "Services" },
              { to: "/packages", label: "Packages" },
              { to: "/portfolio", label: "Portfolio" },
              { to: "/testimonials", label: "Testimonials" },
              { to: "/press", label: "Press" },
              { to: "/blog", label: "Insights" },
              { to: "/intake", label: "Client Intake" },
              { to: "/admin/login", label: "Admin" },
            ].map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="hover:text-[var(--crest-gold)]" data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g,"-")}`}>{l.label}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-4">
          <div className="eyebrow text-[var(--crest-gold)] mb-4">Stay in the Loop</div>
          <p className="text-sm text-[var(--crest-cream)]/70 mb-4">Editorial dispatches, case studies, and brand intelligence — direct from the studio.</p>
          <form onSubmit={submit} className="flex items-center gap-2 border-b border-[var(--crest-cream)]/30">
            <input
              type="email"
              required
              placeholder="Your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-transparent flex-1 py-3 text-sm focus:outline-none placeholder:text-[var(--crest-cream)]/40"
              data-testid="newsletter-email"
            />
            <button className="text-xs tracking-widest uppercase pb-3" style={{ color: "var(--crest-gold)" }} data-testid="newsletter-submit">
              Subscribe
            </button>
          </form>
          {status && <div className="text-xs mt-3" style={{ color: "var(--crest-gold)" }} data-testid="newsletter-status">{status}</div>}
          <div className="mt-8 flex items-center gap-3">
            <a href={COMPANY.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" className="w-10 h-10 border border-[var(--crest-cream)]/25 flex items-center justify-center hover:border-[var(--crest-gold)] hover:text-[var(--crest-gold)] transition-colors" data-testid="footer-instagram">
              <Instagram size={16} />
            </a>
            <a href={COMPANY.facebook} target="_blank" rel="noreferrer" aria-label="Facebook" className="w-10 h-10 border border-[var(--crest-cream)]/25 flex items-center justify-center hover:border-[var(--crest-gold)] hover:text-[var(--crest-gold)] transition-colors" data-testid="footer-facebook">
              <Facebook size={16} />
            </a>
            <a href={COMPANY.tiktok} target="_blank" rel="noreferrer" aria-label="TikTok" className="w-10 h-10 border border-[var(--crest-cream)]/25 flex items-center justify-center hover:border-[var(--crest-gold)] hover:text-[var(--crest-gold)] transition-colors" data-testid="footer-tiktok">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M19.6 6.3a4.7 4.7 0 0 1-3.4-1.7V15a5.5 5.5 0 1 1-5.5-5.5h.4v2.7h-.4a2.8 2.8 0 1 0 2.8 2.8V2h2.7a4.7 4.7 0 0 0 3.4 4.3z"/></svg>
            </a>
          </div>
        </div>
      </div>
      <div className="border-t border-[var(--crest-cream)]/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs tracking-[0.2em] uppercase text-[var(--crest-cream)]/50">
          <div>© {new Date().getFullYear()} Upper Crest Image, LLC. All rights reserved.</div>
          <div data-testid="footer-credit">
            <span>Brought to Life by</span>
            <a href="https://vzngfx.com" target="_blank" rel="noreferrer" className="text-[var(--crest-gold)] hover:underline ml-2" data-testid="footer-vision-link">Vision</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
